import { User } from '../proto/generated/identity'
import throttle from 'lodash/throttle';


const ANALYTICS_THROTTLE_TIMING = 60000;

const throttles = new Map<string, Function>();
// mixpanel-browser is loaded on demand (dynamic import) so it stays out of the eager bundle;
// `mixpanel` is only set once the library has loaded and been initialised.
let mixpanel: (typeof import('mixpanel-browser'))['default'] | undefined;
let initialized = false;
let initializing = false;
const eventQueue: Array<{ eventType: string; eventProperties?: any }> = [];
let pendingUser: Partial<User> | undefined;

function getThrottle(event: string) {
    if (!throttles.has(event)) {
        throttles.set(
            event,
            throttle(
                (props?: any) => mixpanel?.track(event, props),
                ANALYTICS_THROTTLE_TIMING,
                { leading: true, trailing: true }
            )
        );
    }
    return throttles.get(event)!;
}

const WindowClickTracker: (e: Event) => any = (e: Event) => {
    let target = e.target as HTMLElement
    while (!target.hasAttribute('data-event') && target.parentElement) {
        target = target.parentElement
    }
    // Only Handling buttons for now.
    if (target.hasAttribute('data-event')) {
        const eventType = target.getAttribute('data-event')!!
        const eventProperties = Array.from(target.attributes).filter(attr => attr.name.startsWith('data-event-')).reduce((acc, attr) => {
            const propName = attr.name.substring('data-event-'.length)
            acc[propName] = attr.value
            return acc
        }, {} as Record<string, string>)
        eventProperties.resolution = `${window.screen.width}x${window.screen.height}`
        AnalyticsManager.logEvent(eventType, eventProperties)
    }
}

async function initMixpanel() {
    if (!import.meta.env.VITE_MIXPANEL_KEY || initialized || initializing) return
    initializing = true
    try {
        mixpanel = (await import('mixpanel-browser')).default
    } catch {
        // Analytics is optional; keep queuing silently if the chunk fails to load.
        initializing = false
        return
    }
    initialized = true
    mixpanel.init(import.meta.env.VITE_MIXPANEL_KEY!, {
        debug: import.meta.env.VITE_ENV !== 'production',
        track_pageview: false,
        persistence: 'localStorage',
        api_host: 'https://api-eu.mixpanel.com',
    })
    // @ts-ignore
    window.mixpanel = mixpanel
    mixpanel.track('Page Loaded', {
        page: window.location.pathname,
        referrer: document.referrer
    })
    eventQueue.forEach(({ eventType, eventProperties }) => getThrottle(eventType)(eventProperties))
    eventQueue.length = 0
    if (pendingUser) {
        AnalyticsManager.setUser(pendingUser)
        pendingUser = undefined
    }
    window.addEventListener('click', WindowClickTracker, true)
}

class AnalyticsManager {

    static {
        if (import.meta.env.VITE_MIXPANEL_KEY) {
            // window.addEventListener('tick2trade:criticalDataLoaded', initMixpanel)
            window.addEventListener('load', () => {
                if (!initialized) setTimeout(initMixpanel, 5000)
            })
        }
    }

    static configureDevice = (_deviceId: string) => {
    }

    static setUser = (user?: Partial<User>) => {
        if (user) {
            if (!initialized) {
                pendingUser = user
                return
            }
            const newUser: Record<string, any> = {
                '$name': user.name ?? '',
                '$email': user.email ?? '',
                '$mobile': user.mobile,
                '$countryCode': user.countryCode
            }
            const utmParams = window.sessionStorage.getItem('utm_params')
            if (utmParams) {
                const utmObj: Record<string, string> = JSON.parse(utmParams)
                Object.keys(utmObj).forEach(key => {
                    newUser[`${key}`] = utmObj[key]
                })
            }
            mixpanel?.people.set(newUser)
            mixpanel?.identify(user.email)
        }
    }

    static logEvent = (eventType: string, eventProperties: {} = {}) => {
        if (!initialized) {
            eventQueue.push({ eventType, eventProperties })
            return
        }
        getThrottle(eventType)(eventProperties)
    }

    static reset = () => {
        if (initialized) {
            mixpanel?.reset()
            throttles.clear()
        }
    }
}

export default AnalyticsManager
